import { AuthActivityNames } from '@core/activities';
import { AuthClaims, AuthRoles } from '@core/auth';
import { getDevice, getPlatform, getUserAgent } from '@core/utils/env';
import { createActivity } from '@web/features/activities/create-activity';
import * as React from 'react';
import { TMApi } from '@web/services/tmapi';
import useAppBridge from '@web/components/app-bridge';
import {
  AuthPayloads,
  authReducer,
  AuthState,
  AuthStatus,
  AuthTypes,
  initialAuthState,
} from './auth.state';
import { TMUser } from './user';

export const AuthStateContext =
  React.createContext<AuthState>(initialAuthState);

export const AuthDispatchContext = React.createContext<React.Dispatch<{
  type: AuthTypes;
  payload?: AuthPayloads;
}> | null>(null);

export type AuthProviderProps = { children: React.ReactNode };
/**
 * # AuthProvider
 *
 * @remarks
 * Responsible for managing authentication state across the entire application.
 * Located in {@link _app.tsx}, this component will provide global auth state.
 *
 *
 * Use hook {@link useAuth} to access it's state and methods.
 *
 * @note {@link useUserData} is dependent upon this
 *
 * @export
 * @param {AuthProviderProps} { children }
 * @return {JSX.Element}
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = React.useReducer(authReducer, initialAuthState);
  const { notifyUserAuthenticated } = useAppBridge();

  React.useEffect(() => {
    if (state.status === AuthStatus.FULFILLED) {
      notifyUserAuthenticated();
    }
  }, [state, notifyUserAuthenticated]);

  const handleAuthStateChange = React.useCallback(
    async (user: TMUser | null) => {
      if (!user) {
        // no user found
        return dispatch({ type: AuthTypes.ON_AUTH_RESET });
      }

      const claims = {
        role: AuthRoles.USER,
        auth_time: new Date().getTime(),
        email: user.email,
        user_id: user.id,
        email_verified: user.emailVerified,
      } as AuthClaims;

      const details = {
        user_agent: getUserAgent(),
        platform: getPlatform(),
        device: getDevice(),
        timezone_offset: new Date().getTimezoneOffset(),
      };

      // user has signed-in or is remembered
      // best-effort: logging this activity shouldn't block or fail auth
      // (e.g. the session can become invalid mid-flight, such as during
      // account deletion)
      await createActivity({
        uid: user.id,
        config: { name: AuthActivityNames.SIGN_IN, details },
      }).catch((err) => {
        console.log(err);
      });

      return dispatch({
        type: AuthTypes.ON_AUTH_FULFILLED,
        payload: { user, claims },
      });
    },
    [],
  );

  React.useEffect(() => {
    dispatch({ type: AuthTypes.ON_AUTH_FETCH });
    TMApi.restoreLogin(handleAuthStateChange);
  }, [handleAuthStateChange]);

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
}
